import { ElementType } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { DataEventProps } from "@/libraries/wpl/foundations/src/data-event-props";
import { IconSvgProps } from "@/libraries/wpl/foundations/src/icons/types";

export type InfoCardProps = {
    readonly Icon: ElementType<IconSvgProps>;
    readonly iconSize?: string;
    readonly title: string;
    readonly paragraph: string;
    readonly callToActionUrl: string;
    readonly dataEventProps?: DataEventProps;
};

const infoCardContainerStyles = css({
    "display": "flex",
    "width": "280px",
    "minHeight": "249px",
    "padding": `${token("space.0")} ${token("space.300")}`,
    "flexDirection": "column",
    "justifyContent": "center",
    "alignItems": "flex-start",
    "gap": "10px",
    "flexShrink": "0",
    "borderRadius": `${token("space.100")}`,
    "background": "#FFF",
    "border": "1px solid #ccc",
    "transition": "box-shadow 0.3s ease",
    ":hover": {
        boxShadow:
            // eslint-disable-next-line
            "0px 4px 8px rgba(9, 30, 66, 0.1), 0px 0px 16px rgba(9, 30, 66, 0.2)"
    }
});

const titleStyles = css({
    margin: 0,
    lineHeight: "30px" /* 125% */
});

const paragraphStyles = css({
    minHeight: "72px",
    margin: 0,
    marginBlockEnd: "auto",
    lineHeight: "24px" /* 150% */
});

const iconContainerStyles = css({
    marginBlockStart: "20px"
});

export const InfoCard = ({
    Icon,
    title,
    paragraph,
    callToActionUrl,
    iconSize = "48",
    dataEventProps
}: InfoCardProps) => {
    const isExternalHref = callToActionUrl.startsWith("https://");

    return (
        <a
            href={callToActionUrl}
            target={isExternalHref ? "_blank" : undefined}
            rel={isExternalHref ? "noreferrer noopener" : undefined}
            {...dataEventProps}
        >
            <div css={infoCardContainerStyles}>
                <div css={iconContainerStyles}>
                    <Icon size={iconSize} />
                </div>
                <h3 className="h4" css={titleStyles}>
                    {title}
                </h3>
                <p css={paragraphStyles}>{paragraph}</p>
            </div>
        </a>
    );
};
